import { useCallback, useEffect, useMemo, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { rechenwegText, type RechenwegQuelle } from '@symphenia/calc-core'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { frage } from '@/lib/dialoge'
import { inlineInput } from '@/lib/form-styles'
import { FundstelleDialog } from './FundstelleDialog'
import { fetchKiLaeufe, fetchKiLauf, kiBefundEntscheiden, kiUebernehmen, kiVerwerfen, type KiBefund, type KiLauf, type KiSicherheit, type KiZeile } from '../api/calculation-ai.api'

const SICHERHEIT: Record<KiSicherheit, { text: string; klasse: string }> = {
  HIGH: { text: 'hoch', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  MEDIUM: { text: 'mittel', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  LOW: { text: 'niedrig', klasse: 'bg-amber-500/20 text-amber-800 dark:text-amber-300' },
}
const ART: Record<string, string> = {
  COST_LINE: 'Kostenzeile',
  WORK_STEP: 'Arbeitsschritt',
  POSITION_WITH_SHEET: 'neue Position',
  GAP_ANSWER: 'Bieterlücke',
  CHECK_NOTE: 'Hinweis',
  GAP_MISSING: 'fehlt',
  PRICE: 'Preis',
  PATCH: 'Änderung',
}
const AUSFUEHRER = { SYMPHENIA: 'Symphenia', EXTERNAL: 'eigene KI' }
const zahl = (x: number, stellen = 3) => x.toLocaleString('de-DE', { maximumFractionDigits: stellen })
const leseZahl = (t: string) => {
  const w = Number(t.replace(/\./g, '').replace(',', '.'))
  return t.trim() !== '' && Number.isFinite(w) && w >= 0 ? w : null
}

/** Der Wert, den eine Zeile setzt — Menge, Stunden oder Menge der neuen Position. */
const wertVon = (z: KiZeile): number | null => {
  const p = z.payload
  if (z.kind === 'WORK_STEP') return typeof p.hours === 'number' ? p.hours : null
  if (z.kind === 'COST_LINE' || z.kind === 'POSITION_WITH_SHEET') return typeof p.quantity === 'number' ? p.quantity : null
  if (z.kind === 'PRICE') return typeof p.unitPrice === 'number' ? p.unitPrice : null
  return null
}
const FELD: Record<string, string> = { menge: 'Menge', verschnittProzent: 'Verschnitt %', stunden: 'Stunden', qualifikation: 'Qualifikation', personen: 'Personen' }
const feldWert = (v: unknown) => (typeof v === 'number' ? zahl(v) : String(v))
export const titelVon = (z: KiZeile) => {
  const p = z.payload
  // N8: Feldänderung des Assistenten — „Rohr 40×40: Verschnitt % → 10".
  // N1: Preis aus dem Angebot — „Rohr 40×40×3: 12,50 je m (bisher 9,00) — „QR 40/40/3"".
  if (z.kind === 'PRICE') {
    const pe = Number(p.priceUnit ?? 1)
    const je = `je ${pe > 1 ? `${pe} ` : ''}${String(p.unitCode ?? 'Einheit')}`
    const bisher = typeof p.oldUnitCost === 'number' ? ` (bisher ${geldText(p.oldUnitCost)}${Number(p.oldPriceUnit ?? 1) > 1 ? ` je ${p.oldPriceUnit}` : ''})` : ''
    const rabatt = typeof p.discountPercent === 'number' && p.discountPercent ? `, ${zahl(p.discountPercent)} % Rabatt` : ''
    return `${String(p.name ?? '')}: ${geldText(Number(p.unitPrice))} ${je}${rabatt}${bisher} — „${String(p.offerText ?? '')}"`
  }
  if (z.kind === 'PATCH') {
    const felder = Object.entries((p.fields ?? {}) as Record<string, unknown>).map(([k, v]) => `${FELD[k] ?? k} → ${feldWert(v)}`)
    return `${String(p.label ?? '')}: ${felder.join(', ')}`
  }
  if (z.kind === 'COST_LINE') return String(p.name ?? '')
  if (z.kind === 'WORK_STEP') return String(p.activityName ?? '')
  if (z.kind === 'POSITION_WITH_SHEET') return `${p.groupName ? `${String(p.groupName)} › ` : ''}${String(p.shortText ?? '')}`
  if (z.kind === 'GAP_ANSWER') {
    const aus: Record<string, string> = { ARTIKEL: 'aus Artikel', BEFUND: 'aus Befund', ERFAHRUNG: 'wie früher beantwortet', DATEI: 'aus Unterlage' }
    const quelle = typeof p.sourceKind === 'string' && aus[p.sourceKind] ? ` (${aus[p.sourceKind]})` : ''
    return `Lücke ${String(p.seq)}: ${String(p.answer ?? '')}${quelle}`
  }
  return String(p.text ?? '')
}

/**
 * AP-37 Detailblatt N 9. — **Vorschlagsliste**: je Zielposition die Vorschläge mit Häkchen,
 * Rechenweg, Sicherheit und Herkunft; Werte vor dem Übernehmen änderbar („geändert").
 * Übernehmen in einer Transaktion; nichts gelangt ohne Häkchen in die Kalkulation.
 * Zweiter Reiter: **Befunde** bestätigen, berichtigen oder verwerfen (5.4).
 */
export function KiVorschlaege({ kalkulationId, darf, onClose, onGeaendert }: { kalkulationId: string; darf: boolean; onClose: () => void; onGeaendert: () => void }) {
  const [laeufe, setLaeufe] = useState<KiLauf[]>([])
  const [laufId, setLaufId] = useState<string | null>(null)
  const [lauf, setLauf] = useState<(KiLauf & { lines: KiZeile[]; findings: KiBefund[] }) | null>(null)
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [werte, setWerte] = useState<Record<string, string>>({})
  const [reiter, setReiter] = useState<'vorschlaege' | 'befunde'>('vorschlaege')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fundstelle, setFundstelle] = useState<RechenwegQuelle | null>(null)

  const ladeLaeufe = useCallback(async () => {
    const r = await fetchKiLaeufe(kalkulationId)
    setLaeufe(r.data)
    setLaufId((alt) => alt ?? r.data.find((l) => (l.openLines ?? 0) > 0)?.id ?? r.data[0]?.id ?? null)
  }, [kalkulationId])
  useEffect(() => {
    void ladeLaeufe().catch((e) => setFehler(apiErrorText(e, 'Die KI-Läufe ließen sich nicht laden.')))
  }, [ladeLaeufe])
  useEffect(() => {
    if (!laufId) return
    void fetchKiLauf(kalkulationId, laufId)
      .then((l) => {
        setLauf(l)
        setHaken(new Set(l.lines.filter((z) => z.status === 'OPEN' && z.preselected).map((z) => z.id)))
        setWerte({})
      })
      .catch((e) => setFehler(apiErrorText(e, 'Der Lauf ließ sich nicht laden.')))
  }, [kalkulationId, laufId])

  const offen = useMemo(() => lauf?.lines.filter((z) => z.status === 'OPEN') ?? [], [lauf])
  // Gruppiert nach Zielposition; Blattzeilen stehen unter ihrer neuen Position (6.3).
  const gruppen = useMemo(() => {
    const m = new Map<string, { titel: string; zeilen: KiZeile[] }>()
    for (const z of offen.filter((x) => !x.parentLineId)) {
      const schluessel = z.kind === 'POSITION_WITH_SHEET' ? `neu:${z.id}` : (z.targetPositionId ?? 'allgemein')
      const titel =
        z.kind === 'POSITION_WITH_SHEET'
          ? `Neue Position: ${titelVon(z)}`
          : z.position
            ? `${z.position.label} ${z.position.shortText}${z.position.deleted ? ' (gelöscht)' : ''}`
            : 'Allgemein'
      const g = m.get(schluessel) ?? { titel, zeilen: [] }
      g.zeilen.push(z, ...offen.filter((x) => x.parentLineId === z.id))
      m.set(schluessel, g)
    }
    return [...m.values()]
  }, [offen])

  const umschalten = (z: KiZeile) =>
    setHaken((alt) => {
      const neu = new Set(alt)
      const an = !neu.has(z.id)
      const kinder = offen.filter((x) => x.parentLineId === z.id).map((x) => x.id)
      for (const id of [z.id, ...kinder]) an ? neu.add(id) : neu.delete(id)
      if (an && z.parentLineId) neu.add(z.parentLineId)
      return neu
    })

  async function tu(schritt: () => Promise<unknown>) {
    setLaeuft(true)
    try {
      setFehler(null)
      await schritt()
      await ladeLaeufe()
      if (laufId) {
        const l = await fetchKiLauf(kalkulationId, laufId)
        setLauf(l)
        setHaken(new Set(l.lines.filter((z) => z.status === 'OPEN' && z.preselected).map((z) => z.id)))
      }
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das ließ sich nicht ausführen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const uebernehmen = () => {
    if (!laufId) return
    const edits = Object.entries(werte)
      .filter(([id]) => haken.has(id))
      .map(([lineId, t]) => ({ lineId, value: leseZahl(t) }))
      .filter((e): e is { lineId: string; value: number } => e.value !== null)
    return tu(() => kiUebernehmen(kalkulationId, laufId, [...haken], edits))
  }
  const verwerfen = () => laufId && tu(() => kiVerwerfen(kalkulationId, laufId, [...haken]))

  const bericht = lauf?.report
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div
        className="my-8 w-full max-w-4xl space-y-3 rounded-lg border border-border p-4 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Sparkles className="h-4 w-4" /> KI-Vorschläge
          </h2>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select className={`${inlineInput} !w-auto`} value={laufId ?? ''} onChange={(e) => setLaufId(e.target.value || null)} aria-label="Lauf">
            {laeufe.map((l) => (
              <option key={l.id} value={l.id}>
                {new Date(l.createdAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} · {l.capability} · {AUSFUEHRER[l.executor]}
                {l.openLines ? ` · ${l.openLines} offen` : ''}
                {l.status === 'REJECTED' ? ' · abgelehnt' : ''}
              </option>
            ))}
          </select>
          <div className="ml-auto flex gap-1">
            {(['vorschlaege', 'befunde'] as const).map((r) => (
              <button key={r} type="button" className={`rounded-md px-2.5 py-1 ${reiter === r ? 'bg-primary text-primary-foreground' : 'border border-border'}`} onClick={() => setReiter(r)}>
                {r === 'vorschlaege' ? `Vorschläge (${offen.length})` : `Befunde (${lauf?.findings.length ?? 0})`}
              </button>
            ))}
          </div>
        </div>
        {lauf && (
          <p className="text-xs text-muted-foreground">
            {lauf.executor === 'EXTERNAL' ? `Eigene KI${lauf.model ? ` (laut Ergebnisdatei: ${lauf.model})` : ''}` : `Symphenia${lauf.model ? ` · ${lauf.model}` : ''}`}
            {bericht?.accepted !== undefined && ` · ${bericht.accepted} Vorschläge geprüft`}
            {bericht?.dropped?.length ? ` · ${bericht.dropped.length} vom Prüfer verworfen` : ''}
            {lauf.error ? ` · ${laufFehlerText(lauf.error)}` : ''}
          </p>
        )}
        {bericht?.dropped && bericht.dropped.length > 0 && (
          <details className="rounded-md border border-border/60 p-2 text-xs">
            <summary className="cursor-pointer">Vom Prüfer verworfen</summary>
            <ul className="mt-1 space-y-0.5">
              {bericht.dropped.map((d) => (
                <li key={d.ref}>
                  <span className="font-mono">{d.ref}</span> — {d.text}
                  {d.detail ? ` (${d.detail})` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}

        {fundstelle?.fileId && (
          <FundstelleDialog
            kalkulationId={kalkulationId}
            fileId={fundstelle.fileId}
            seite={fundstelle.seite ?? 1}
            rect={fundstelle.art === 'PLAN' ? (fundstelle.rect ?? null) : null}
            zitat={fundstelle.zitat ?? null}
            onClose={() => setFundstelle(null)}
          />
        )}
        {reiter === 'vorschlaege' && (
          <>
            {!offen.length && <p className="py-6 text-center text-sm text-muted-foreground">Keine offenen Vorschläge in diesem Lauf.</p>}
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {gruppen.map((g) => (
                <div key={g.titel} className="rounded-md border border-border/60">
                  <div className="border-b border-border/60 bg-muted/40 px-3 py-1.5 text-sm font-medium">{g.titel}</div>
                  <ul className="divide-y divide-border/60">
                    {g.zeilen.map((z) => {
                      const w = wertVon(z)
                      return (
                        <li key={z.id} className={`flex items-start gap-2 px-3 py-2 text-sm ${z.parentLineId ? 'pl-8' : ''}`}>
                          <input type="checkbox" className="mt-1" checked={haken.has(z.id)} disabled={!darf} onChange={() => umschalten(z)} aria-label={`${ART[z.kind]} übernehmen`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-muted-foreground">{ART[z.kind]}</span>
                              <span className="font-medium">{titelVon(z)}</span>
                              <span className={`rounded px-1.5 text-xs ${SICHERHEIT[z.confidence].klasse}`}>{SICHERHEIT[z.confidence].text}</span>
                            </div>
                            {z.derivation && <div className="text-xs tabular-nums text-muted-foreground">{rechenwegText(z.derivation, z.kind === 'WORK_STEP' ? 'h' : String(z.payload.unitCode ?? ''))}</div>}
                            {z.derivation?.begruendung && <div className="text-xs text-muted-foreground">{z.derivation.begruendung}</div>}
                            {typeof z.payload.reason === 'string' && z.payload.reason && <div className="text-xs text-muted-foreground">{z.payload.reason}</div>}
                            {z.notes.length > 0 && <div className="text-xs text-amber-700 dark:text-amber-400">{z.notes.join(' · ')}</div>}
                            <Fundstellen
                              quellen={
                                z.kind === 'PRICE' && typeof z.payload.fileId === 'string'
                                  ? [{ art: 'DATEI', fileId: z.payload.fileId, seite: Number(z.payload.page ?? 1), zitat: (z.payload.quote as string | null) ?? undefined } as RechenwegQuelle]
                                  : Object.values(z.derivation?.groessen ?? {}).map((g) => g.quelle)
                              }
                              onOeffnen={setFundstelle}
                            />
                          </div>
                          {w !== null && (
                            <label className="flex items-center gap-1 text-xs text-muted-foreground">
                              {z.kind === 'WORK_STEP' ? 'h' : z.kind === 'PRICE' ? 'EP' : 'Menge'}
                              <input
                                className={`${inlineInput} !w-24 text-right`}
                                inputMode="decimal"
                                disabled={!darf}
                                value={werte[z.id] ?? zahl(w)}
                                onChange={(e) => setWerte({ ...werte, [z.id]: e.target.value })}
                              />
                              {werte[z.id] !== undefined && leseZahl(werte[z.id]) !== w && <span className="text-amber-700 dark:text-amber-400">geändert</span>}
                            </label>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
            {darf && offen.length > 0 && (
              <div className="flex justify-end gap-2">
                <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50" disabled={!haken.size || laeuft} onClick={() => void verwerfen()}>
                  Markierte verwerfen
                </button>
                <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!haken.size || laeuft} onClick={() => void uebernehmen()}>
                  {haken.size} übernehmen
                </button>
              </div>
            )}
          </>
        )}

        {reiter === 'befunde' && lauf && (
          <ul className="max-h-[60vh] divide-y divide-border/60 overflow-y-auto rounded-md border border-border/60 text-sm">
            {!lauf.findings.length && <li className="px-3 py-6 text-center text-muted-foreground">Keine Befunde.</li>}
            {lauf.findings.map((b) => (
              <li key={b.id} className="flex items-start gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">{b.topic}</span>
                    <span className={b.status === 'REJECTED' ? 'line-through' : ''}>{b.text}</span>
                    {b.value !== null && (
                      <span className="tabular-nums">
                        {b.status === 'CORRECTED' && b.correctedValue !== null ? (
                          <>
                            <s>{zahl(b.value)}</s> {zahl(b.correctedValue)}
                          </>
                        ) : (
                          zahl(b.value)
                        )}{' '}
                        {b.unit ?? ''}
                      </span>
                    )}
                    <span className={`rounded px-1.5 text-xs ${SICHERHEIT[b.confidence].klasse}`}>{SICHERHEIT[b.confidence].text}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Quelle: {b.source.art}
                    {b.source.oz ? ` ${b.source.oz}` : ''}
                    {b.source.seite ? `, Seite ${b.source.seite}` : ''}
                    {b.source.zitat ? ` — „${b.source.zitat}"` : ''}
                  </div>
                  <Fundstellen quellen={[b.source]} onOeffnen={setFundstelle} />
                </div>
                {darf && b.status !== 'REJECTED' && (
                  <div className="flex gap-2 text-xs">
                    {b.status !== 'CONFIRMED' && (
                      <button type="button" className="text-primary hover:underline" onClick={() => void tu(() => kiBefundEntscheiden(kalkulationId, b.id, { status: 'CONFIRMED' }))}>
                        bestätigen
                      </button>
                    )}
                    {b.value !== null && (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={async () => {
                          const t = await frage(`Richtiger Wert für „${b.text}"`, zahl(b.correctedValue ?? b.value ?? 0), { ok: 'Berichtigen' })
                          const v = t === null ? null : leseZahl(t)
                          if (v !== null) void tu(() => kiBefundEntscheiden(kalkulationId, b.id, { status: 'CORRECTED', correctedValue: v }))
                        }}
                      >
                        berichtigen
                      </button>
                    )}
                    <button type="button" className="text-destructive hover:underline" onClick={() => void tu(() => kiBefundEntscheiden(kalkulationId, b.id, { status: 'REJECTED' }))}>
                      verwerfen
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** Quellen `DATEI`/`PLAN` als Sprung zur Fundstelle (Seite bzw. Plan-Ausschnitt). */
function Fundstellen({ quellen, onOeffnen }: { quellen: RechenwegQuelle[]; onOeffnen: (q: RechenwegQuelle) => void }) {
  // Dieselbe Fundstelle für zwei Größen (Stück und Länge aus derselben Zeile) nur einmal.
  const gesehen = new Set<string>()
  const mitDatei = quellen.filter((q) => {
    const schluessel = `${q.art}|${q.fileId}|${q.seite ?? ''}|${q.feld ?? ''}|${q.zitat ?? ''}`
    if (!((q.art === 'DATEI' || q.art === 'PLAN') && q.fileId) || gesehen.has(schluessel)) return false
    gesehen.add(schluessel)
    return true
  })
  if (!mitDatei.length) return null
  return (
    <div className="mt-0.5 flex flex-wrap gap-2 text-xs">
      {mitDatei.map((q, i) =>
        // N2: eine Tabellenzeile (Excel/CSV) hat keine Seite — sie steht als Text da, nicht als PDF-Fundstelle.
        q.feld && !q.seite ? (
          <span key={i} className="text-muted-foreground">
            {q.feld}
          </span>
        ) : (
        <button key={i} type="button" className="text-primary hover:underline" onClick={() => onOeffnen(q)}>
          {q.art === 'PLAN' ? 'Plan' : 'Unterlage'}, Seite {q.seite ?? 1}
          {q.zitat ? ` „${q.zitat}"` : ''}
        </button>
        ),
      )}
    </div>
  )
}
